// app/javascript/controllers/admin_nav_controller.js
//
// Drives the admin command palette (⌘K) and focuses the customer search on
// load. Collapsible nav groups use native <details>/<summary> and need NO JS.
//
// Registered in app/javascript/controllers/index.js:
//   import AdminNavController from "./admin_nav_controller"
//   application.register("admin-nav", AdminNavController)

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["customerSearch", "palette", "paletteInput", "option", "sidebar", "sidebarBackdrop"]

  connect() {
    this.selectedIndex = 0
    this._onKey = this.onGlobalKey.bind(this)
    document.addEventListener("keydown", this._onKey)
    // Drop the cursor into the customer search the moment the screen loads.
    if (this.hasCustomerSearchTarget) {
      requestAnimationFrame(() => this.customerSearchTarget.focus())
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
  }

  // Filter dropdowns live in a separate form from the topbar search. Copy the
  // current query into that form before it submits so a filter change keeps
  // whatever is typed in the search bar.
  submitWithSearch(event) {
    const form = event.currentTarget
    if (!(form instanceof HTMLFormElement)) return

    if (this.hasCustomerSearchTarget) {
      const qField = form.querySelector('input[type="hidden"][name="q"]')
      if (qField) qField.value = this.customerSearchTarget.value
    }

    form.requestSubmit()
  }

  // ── Palette open / close ────────────────────────────────────────────────
  open() {
    this.paletteTarget.hidden = false
    this.selectedIndex = 0
    this.render()
    requestAnimationFrame(() => this.paletteInputTarget.focus())
  }

  close() {
    this.paletteTarget.hidden = true
  }

  stop(event) {
    event.stopPropagation()
  }

  backdropClose(event) {
    if (event.target === this.paletteTarget) this.close()
  }

  // ── Mobile sidebar (off-canvas drawer) ──────────────────────────────────
  openSidebar() {
    if (this.hasSidebarTarget) this.sidebarTarget.classList.add("is-open")
    if (this.hasSidebarBackdropTarget) this.sidebarBackdropTarget.hidden = false
  }

  closeSidebar() {
    if (this.hasSidebarTarget) this.sidebarTarget.classList.remove("is-open")
    if (this.hasSidebarBackdropTarget) this.sidebarBackdropTarget.hidden = true
  }

  toggleSidebar() {
    if (this.hasSidebarTarget && this.sidebarTarget.classList.contains("is-open")) {
      this.closeSidebar()
    } else {
      this.openSidebar()
    }
  }

  // Close the drawer when a real nav link (not a <details> summary) is tapped.
  navClick(event) {
    if (event.target.closest("a")) this.closeSidebar()
  }

  // ── Filtering ───────────────────────────────────────────────────────────
  filter() {
    const q = this.paletteInputTarget.value.trim().toLowerCase()
    this.optionTargets.forEach((option) => {
      option.hidden = q.length > 0 && !option.dataset.label.includes(q)
    })
    this.selectedIndex = 0
    this.render()
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden)
  }

  render() {
    const options = this.visibleOptions()
    this.optionTargets.forEach((o) => o.classList.remove("is-selected"))
    const current = options[this.selectedIndex]
    if (current) current.classList.add("is-selected")
  }

  // ── Keyboard ──────────────────────────────────────────────────────────────
  onGlobalKey(event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault()
      this.open()
      return
    }
    if (event.key === "Escape") {
      this.closeSidebar()
      if (!this.paletteTarget.hidden) this.close()
      return
    }
    if (this.paletteTarget.hidden) return

    const options = this.visibleOptions()
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, options.length - 1)
        this.render()
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.render()
        break
      case "Enter": {
        event.preventDefault()
        const selected = options[this.selectedIndex]
        if (selected) selected.click()
        break
      }
    }
  }
}
